import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, DollarSign, Eye, Lock, AlertTriangle, TrendingUp, Users, Clock, ChevronRight, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== FUGITIVES NEAR YOU ====================
export const FugitivesNearYou = () => {
  const navigate = useNavigate();
  const [location, setLocation] = useState({ city: 'Your Area', state: '' });
  const [fugitives, setFugitives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get user's approximate location
    const detectLocation = async () => {
      try {
        // Use IP-based geolocation (no permission needed)
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        if (data.city && data.region) {
          setLocation({ city: data.city, state: data.region });
        }
      } catch (err) {
        // Fallback - use generic text
        setLocation({ city: 'Your Area', state: '' });
      }
    };

    // Fetch some fugitives to display (blurred)
    const fetchFugitives = async () => {
      try {
        const res = await fetch(`${API}/fugitives?limit=10`);
        const data = await res.json();
        // Handle both array response and object with fugitives key
        const fugitivesList = Array.isArray(data) ? data : (data.fugitives || []);
        if (fugitivesList.length > 0) {
          // Get top 3 with highest rewards
          const sorted = fugitivesList
            .filter(f => f.reward_amount || f.reward_text || f.reward)
            .sort((a, b) => {
              const getReward = (f) => {
                if (typeof f.reward_amount === 'number') return f.reward_amount;
                if (typeof f.reward === 'number') return f.reward;
                const match = (f.reward_text || '').match(/\$?([\d,]+)/);
                return match ? parseInt(match[1].replace(/,/g, '')) : 0;
              };
              return getReward(b) - getReward(a);
            })
            .slice(0, 3);
          setFugitives(sorted);
        }
      } catch (err) {
        console.error('Failed to fetch fugitives:', err);
      } finally {
        setLoading(false);
      }
    };

    detectLocation();
    fetchFugitives();
  }, []);

  const formatReward = (fugitive) => {
    if (fugitive.reward_amount) return `$${fugitive.reward_amount.toLocaleString()}`;
    if (fugitive.reward_text) return fugitive.reward_text;
    if (typeof fugitive.reward === 'number') {
      return `$${fugitive.reward.toLocaleString()}`;
    }
    return 'Reward Available';
  };

  if (loading) {
    return (
      <div className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-700 rounded w-64 mx-auto mb-4"></div>
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-48 bg-slate-800 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="py-16 px-4 bg-gradient-to-b from-slate-950 to-slate-900 border-t border-b border-red-500/20">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-4 py-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-red-400 animate-pulse" />
            <span className="text-red-400 text-sm font-medium">Active Fugitives Detected</span>
          </div>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-white mb-2">
            Fugitives Near <span className="text-red-400">{location.city}{location.state ? `, ${location.state}` : ''}</span>
          </h2>
          <p className="text-slate-400">These individuals may be in your area right now</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {fugitives.map((fugitive, idx) => (
            <motion.div
              key={fugitive.uid || idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="relative group cursor-pointer"
              onClick={() => navigate('/login')}
            >
              {/* Blurred Card */}
              <div className="card-tactical overflow-hidden">
                {/* Blurred Image */}
                <div className="relative h-48 overflow-hidden">
                  {fugitive.images?.[0]?.large || fugitive.image_url ? (
                    <img
                      src={fugitive.images?.[0]?.large || fugitive.image_url}
                      alt="Fugitive"
                      className="w-full h-full object-cover blur-xl scale-110"
                    />
                  ) : (
                    <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                      <Eye className="w-12 h-12 text-slate-600 blur-sm" />
                    </div>
                  )}
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center">
                    <div className="text-center">
                      <Lock className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                      <p className="text-white font-semibold text-sm">Sign up to reveal</p>
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-500 text-sm blur-sm select-none">████████ ████</span>
                    <span className="bg-red-500/20 text-red-400 text-xs px-2 py-1 rounded">WANTED</span>
                  </div>
                  <div className="flex items-center gap-2 text-amber-400 font-bold">
                    <DollarSign className="w-4 h-4" />
                    <span>{formatReward(fugitive)}</span>
                  </div>
                  <p className="text-slate-500 text-xs mt-1">Your potential share: <span className="text-amber-300">{formatReward(fugitive).includes('$') ? `$${Math.floor(parseInt(formatReward(fugitive).replace(/[^0-9]/g, '')) / 2).toLocaleString()}` : '50%'}</span></p>
                </div>
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 bg-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-sm" />
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <button
            onClick={() => navigate('/login')}
            className="btn-primary text-lg px-8 py-4 flex items-center gap-2 mx-auto"
            data-testid="reveal-fugitives-btn"
          >
            <Eye className="w-5 h-5" />
            Reveal All Fugitives - Free
            <ChevronRight className="w-5 h-5" />
          </button>
          <p className="text-slate-500 text-sm mt-3">Join 10,000+ citizens tracking fugitives</p>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== LIVE ACTIVITY FEED ====================
export const LiveActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasRealData, setHasRealData] = useState(false);

  // Fallback data generator for when there's no real activity
  const generateFallbackActivity = () => {
    const states = ['Texas', 'California', 'Florida', 'New York', 'Arizona', 'Georgia', 'Ohio', 'Pennsylvania', 'Illinois', 'Michigan', 'North Carolina', 'Nevada', 'Colorado', 'Virginia', 'Washington'];
    const actions = ['submitted a tip', 'joined the hunt', 'started tracking a fugitive', 'reported a sighting'];
    const timeAgo = ['just now', '2 min ago', '5 min ago', '8 min ago', '12 min ago'];
    
    return {
      id: Math.random().toString(36).substr(2, 9),
      state: states[Math.floor(Math.random() * states.length)],
      action: actions[Math.floor(Math.random() * actions.length)],
      time_ago: timeAgo[Math.floor(Math.random() * timeAgo.length)]
    };
  };

  // Fetch real activity data
  useEffect(() => {
    const fetchRealActivity = async () => {
      try {
        const res = await fetch(`${API}/activity/feed?limit=10`);
        const data = await res.json();
        
        if (data.activities && data.activities.length > 0) {
          // Transform real data to match our display format
          const realActivities = data.activities.map(a => ({
            id: a.id,
            state: a.state,
            action: a.action,
            time_ago: a.time_ago || 'recently',
            isReal: true
          }));
          setActivities(realActivities);
          setHasRealData(true);
        } else {
          // Fall back to generated data if no real activity
          setActivities(Array(6).fill(null).map(generateFallbackActivity));
          setHasRealData(false);
        }
      } catch (err) {
        console.error('Failed to fetch activity feed:', err);
        // Fall back to generated data on error
        setActivities(Array(6).fill(null).map(generateFallbackActivity));
        setHasRealData(false);
      }
    };

    // Initial fetch
    fetchRealActivity();

    // Refresh real data every 30 seconds
    const refreshInterval = setInterval(fetchRealActivity, 30000);

    return () => clearInterval(refreshInterval);
  }, []);

  // If no real data, add simulated new activities periodically
  useEffect(() => {
    if (hasRealData) return; // Don't add fake data if we have real data
    
    const interval = setInterval(() => {
      setActivities(prev => {
        const newActivity = generateFallbackActivity();
        newActivity.time_ago = 'just now';
        return [newActivity, ...prev.slice(0, 5)];
      });
    }, 10000 + Math.random() * 5000);

    return () => clearInterval(interval);
  }, [hasRealData]);

  // Cycle through activities for display
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % Math.max(activities.length, 1));
    }, 4000);
    return () => clearInterval(interval);
  }, [activities.length]);

  const currentActivity = activities[currentIndex];

  return (
    <div className="fixed bottom-4 left-4 z-40 max-w-xs" data-testid="live-activity-feed">
      <AnimatePresence mode="wait">
        {currentActivity && (
          <motion.div
            key={currentActivity.id}
            initial={{ opacity: 0, x: -50, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -50, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-lg p-3 shadow-xl"
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-8 h-8 bg-cyan-500/20 rounded-full flex items-center justify-center">
                  <Users className="w-4 h-4 text-cyan-400" />
                </div>
                <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full animate-pulse ${currentActivity.isReal ? 'bg-green-500' : 'bg-amber-500'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  Anonymous user in <span className="text-cyan-400">{currentActivity.state}</span>
                </p>
                <p className="text-slate-400 text-xs">{currentActivity.action} · {currentActivity.time_ago}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== FEATURED HIGH-VALUE FUGITIVE ====================
export const FeaturedFugitive = () => {
  const navigate = useNavigate();
  const [fugitive, setFugitive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHighValueFugitive = async () => {
      try {
        const res = await fetch(`${API}/fugitives?limit=50`);
        const data = await res.json();
        // Handle both array response and object with fugitives key
        const fugitivesList = Array.isArray(data) ? data : (data.fugitives || []);
        if (fugitivesList.length > 0) {
          // Find highest reward fugitive
          const sorted = fugitivesList
            .filter(f => f.reward_amount || f.reward_text || f.reward)
            .map(f => {
              let rewardValue = 0;
              if (typeof f.reward_amount === 'number') {
                rewardValue = f.reward_amount;
              } else if (typeof f.reward === 'number') {
                rewardValue = f.reward;
              } else if (f.reward_text) {
                const match = f.reward_text.match(/\$?([\d,]+)/);
                if (match) rewardValue = parseInt(match[1].replace(/,/g, ''));
              }
              return { ...f, rewardValue };
            })
            .sort((a, b) => b.rewardValue - a.rewardValue);
          
          if (sorted.length > 0) {
            setFugitive(sorted[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch featured fugitive:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHighValueFugitive();
  }, []);

  if (loading || !fugitive) return null;

  const rewardText = fugitive.reward_text || `$${fugitive.rewardValue?.toLocaleString()}`;
  const yourShare = fugitive.rewardValue ? `$${Math.floor(fugitive.rewardValue / 2).toLocaleString()}` : '50%';

  return (
    <section className="py-16 px-4 bg-gradient-to-r from-amber-500/5 via-slate-900 to-amber-500/5 border-y border-amber-500/20">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-8 items-center"
        >
          {/* Left - Info */}
          <div>
            <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/30 rounded-full px-4 py-2 mb-4">
              <TrendingUp className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium">Highest Reward This Week</span>
            </div>
            
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Featured Fugitive
            </h2>
            
            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Total FBI Reward</p>
                  <p className="text-amber-400 font-bold text-2xl">{rewardText}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <Target className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Your Potential Earnings</p>
                  <p className="text-green-400 font-bold text-2xl">{yourShare}</p>
                </div>
              </div>

              {fugitive.caution && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <p className="text-red-300 text-sm line-clamp-2">{fugitive.caution.replace(/<[^>]*>/g, '').substring(0, 150)}...</p>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => navigate('/login')}
              className="btn-primary text-lg px-6 py-3 flex items-center gap-2"
              data-testid="view-fugitive-btn"
            >
              <Eye className="w-5 h-5" />
              View Full Profile
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Right - Blurred Preview */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="card-tactical overflow-hidden">
              <div className="relative aspect-square max-h-80">
                {fugitive.images?.[0]?.large || fugitive.image_url ? (
                  <img
                    src={fugitive.images?.[0]?.large || fugitive.image_url}
                    alt="Fugitive"
                    className="w-full h-full object-cover blur-2xl scale-110"
                  />
                ) : (
                  <div className="w-full h-full bg-slate-800" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
                
                {/* Lock Overlay */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="bg-slate-900/80 backdrop-blur-sm rounded-lg p-6 text-center">
                    <Lock className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                    <p className="text-white font-semibold mb-1">Identity Protected</p>
                    <p className="text-slate-400 text-sm">Sign up to view full details</p>
                  </div>
                </div>
              </div>

              {/* Stats Bar */}
              <div className="bg-slate-800/50 p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-400 text-sm blur-sm select-none">████████, ██</span>
                </div>
                <div className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded">
                  MOST WANTED
                </div>
              </div>
            </div>

            {/* Animated Border */}
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-red-500/20 to-amber-500/20 rounded-lg blur-sm -z-10 animate-pulse" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ==================== STICKY CTA BAR ====================
export const StickyCTABar = ({ show = true }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling past the hero section (roughly)
      setIsVisible(window.scrollY > 600);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!show || !isVisible) return null;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-t border-cyan-500/30 py-3 px-4 shadow-2xl"
      data-testid="sticky-cta-bar"
    >
      <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
        <div className="hidden sm:flex items-center gap-3">
          <DollarSign className="w-6 h-6 text-amber-400" />
          <div>
            <p className="text-white font-semibold">$200M+ in unclaimed rewards</p>
            <p className="text-slate-400 text-sm">Start earning your share today</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-amber-400 sm:hidden">
          <DollarSign className="w-5 h-5" />
          <span className="font-semibold">$200M+ unclaimed</span>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="btn-primary whitespace-nowrap flex items-center gap-2"
          data-testid="sticky-cta-btn"
        >
          <Target className="w-4 h-4" />
          Join Free
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};
